// Guard component to gate page rendering based on capabilities
import React from 'react';
import type { ReactNode } from 'react';
import { useCan } from './hooks/useCan.js';
import { Alert, Tooltip } from 'antd';
import { LockOutlined } from '@ant-design/icons';

export interface RequireCapabilityProps {
  feature: string;
  action: string;
  tenantId?: string;
  fallback?: ReactNode;
  children?: ReactNode;
}

export function RequireCapability({
  feature,
  action,
  tenantId,
  fallback,
  children,
}: RequireCapabilityProps) {
  const { allowed, loading, reason } = useCan({ feature, action, tenantId });
  if (loading) return <div />;
  if (!allowed) {
    if (fallback) return <div role="alert">{fallback}</div>;
    return (
      <Alert
        role="alert"
        type="warning"
        showIcon
        icon={<LockOutlined />}
        title="Access restricted"
        description={`This page requires the ${feature}:${action} capability. Contact your administrator to request access.`}
        style={{ maxWidth: 600, margin: '48px auto' }}
      />
    );
  }
  const title = reason === 'bypass' ? 'Allowed (bypass)' : 'Allowed';
  // Use display: contents to avoid altering layout
  return (
    <Tooltip title={title}>
      <div style={{ display: 'contents' }}>{children as React.ReactElement}</div>
    </Tooltip>
  );
}

export default RequireCapability;
